import { isLoginAsActiveInStorage } from "../loginAs";
import { isPreviewInternalHostname } from "../url";
import { getEventGroups } from "./analyticsGroups";

// 8k–150k/month each and read by no recent insight, cohort, action or query (REPL-33831); most are v1 editor clicks.
const RETIRED_EVENT_NAMES: ReadonlySet<string> = new Set([
  "agent.beta.promotion.viewed",
  "agent.miniapp.connectionLost",
  "canvas.preview",
  "component.add",
  "component.delete",
  "component.drop",
  "component.hide",
  "components.search",
  "design.modifier.add",
  "editor.panel.resize",
  "editor.panel.toggle",
  "editor.rightbar.interaction.add",
  "editor.rightbar.tab.select",
  "element.select",
  "element.update",
  "error.component.drop",
  "header.viewPage",
  "library.style.apply",
  "preview.requested",
]);

type CapturedEvent = {
  event: string;
  properties: Record<string, unknown>;
};

/** posthog-js `before_send` for both apps: drops, groups, and names every Agent app event `agent.*`. */
export function prepareAnalyticsEvent<Event extends CapturedEvent>(
  event: Event | null,
  {
    storedGroups,
    surface,
    isSynthetic,
  }: {
    storedGroups: Record<string, string>;
    surface: "agent-ui" | "legacy";
    /** Canary, test and CI accounts send nothing, replays included. */
    isSynthetic: boolean;
  },
): Event | null {
  // Branch previews share the production project, so their events would mix into customer metrics.
  if (
    !event ||
    isSynthetic ||
    isPreviewInternalHostname(window.location.hostname)
  ) {
    return null;
  }
  if (event.event === "$snapshot") {
    return event;
  }
  // A superuser signed in as a customer is not that customer; replays (above) still record for support.
  if (isLoginAsActiveInStorage() || RETIRED_EVENT_NAMES.has(event.event)) {
    return null;
  }
  if (event.event === "$groupidentify") {
    return event;
  }
  event.properties.$groups = getEventGroups({
    properties: event.properties,
    storedGroups,
  });
  event.properties.surface ??= surface;
  // Public API and MCP events stay outside agent.*, like the server's public.request.
  if (
    surface === "agent-ui" &&
    !/^(\$|agent\.|public_mcp\.)/.test(event.event)
  ) {
    event.event = event.event.startsWith("agentBilling.")
      ? `agent.billing.${event.event.slice("agentBilling.".length)}`
      : `agent.${event.event}`;
  }
  return event;
}
